import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as CoreReducer from '../reducers';
import * as NotificationActions from '../actions/notification.actions';
import {catchError, exhaustMap, finalize, map, mergeMap, switchMap, takeUntil, tap, withLatestFrom} from 'rxjs/operators';
import { of} from 'rxjs';
import {NotificationService} from '../../../shared/services/notification.service';
import {NotificationStats} from '../../../shared/models/notification-stats';
import {Notification} from '../../../shared/models/notification';
import {Store} from '@ngrx/store';
import {UpdateNotification} from '../../../shared/models/update-notification';
import {NotificationStatus} from '../../../shared/models/notification-status';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';
import { LoadingBarService } from '@ngx-loading-bar/core';
import moment from 'moment-timezone';
import { SnackBarService, SnackBarText } from '../../../shared/components/snack-bar-toast/snack-bar.service';
import { NotificationSeverity } from '../../../shared/models/notification-severity';
import { notificationTitleKey, shareLinkAccess } from '../../../shared/models/notification-title';
import { NotificationsDrawerService } from '../../../shared/services/notifications-drawer.service';
import { StorageService } from '../../../core/storage/storage.service';
import * as AuthApiActions from '../../auth/actions/auth-api.actions';
import * as SigninPageActions from '../../auth/actions/signin-page.actions';

@Injectable()
export class NotificationsEffects {

  notificationsInit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.notificationsInit.type),
      tap(action => {
        this._notificationService.init();
      })
    ),
    { dispatch: false }
  );

  notificationsDisconnect$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.notificationsDisconnect.type),
      tap(action => {
        this._notificationService.disconnect();
      })
    ),
    { dispatch: false }
  );

  getNotifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.getNotifications.type),
      switchMap((action: any) => this._notificationService.getNotifications(action.page, action.filter)
        .pipe(
          takeUntil(this._loggedOut$),
          exhaustMap((data) => {
            const stats = new NotificationStats(data.collectionMetadata.unreadNotifications, data.currentPage, data.totalPages, data.totalItems);
            const notifications = this._notificationService.update(data.items);
            return of(NotificationActions.getNotificationsSuccess({notifications: notifications, stats: stats}));
          }),
          catchError(error => of(NotificationActions.getNotificationsFailure({error})))
        )
      )
    )
  );

  getNotificationByIds$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.getNotificationByIds.type),
      // Only the fetched rows: the reducer merges them into the list as it is when they arrive.
      mergeMap((action: any) => this._notificationService.getNotificationByIds(action.ids)
        .pipe(
          takeUntil(this._loggedOut$),
          map((data) => NotificationActions.getNotificationByIdsSuccess({notifications: this._notificationService.update(data.items)})),
          catchError(error => of(NotificationActions.getNotificationByIdsFailure({error})))
        )
      )
    )
  );

  loadMoreNotifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.loadMoreNotifications.type),
      withLatestFrom(
        this._store$.select(CoreReducer.getNotificationStats),
        this._store$.select(CoreReducer.getNotificationFilter),
      ),
      tap(() => this._loader.start()),
      // The filter goes with the page request: without it the next page would come back unfiltered
      // and the list would mix filtered and unfiltered rows.
      switchMap(([action, stats, filter]) => this._notificationService.getNotifications(stats.currentPage + 1, filter)
        .pipe(
          // A page asked for before the list was reloaded (a new filter, or "mark all as read")
          // describes the old list, so it is dropped rather than appended.
          takeUntil(this._actions$.pipe(ofType(NotificationActions.getNotifications, NotificationActions.markAllNotificationsAsRead))),
          takeUntil(this._loggedOut$),
          map((data) => NotificationActions.loadMoreNotificationsSuccess({
            notifications: this._notificationService.update(data.items),
            stats: new NotificationStats(data.collectionMetadata.unreadNotifications, data.currentPage, data.totalPages, data.totalItems),
          })),
          catchError(error => of(NotificationActions.loadMoreNotificationsFailure({error}))),
          finalize(() => this._loader.complete()),
        )
      )
    )
  );


  updateNotificationsStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.updateNotificationsStatus.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._notificationService.updateNotificationStatus(new UpdateNotification(action.notificationsIds, NotificationStatus.READ))
        .pipe(
          exhaustMap((data) => of(NotificationActions.updateNotificationsStatusSuccess({notifications: data}))),
          catchError(error => of(NotificationActions.updateNotificationsStatusFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  markAllNotificationsAsRead$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.markAllNotificationsAsRead.type),
      tap(() => this._loader.start()),
      switchMap(() => this._notificationService.markAllAsRead()
        .pipe(
          // Reload the first page so the list shows the new statuses; the server has already told
          // every other tab of this user through the hub.
          exhaustMap(() => of(
            NotificationActions.markAllNotificationsAsReadSuccess(),
            NotificationActions.getNotifications({page: 1})
          )),
          catchError(error => of(NotificationActions.markAllNotificationsAsReadFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  setNotificationFilter$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.setNotificationFilter.type),
      // A new filter means a new result set, so start again at the first page.
      switchMap((action: any) => of(NotificationActions.getNotifications({page: 1, filter: action.filter})))
    )
  );

  pushNotification$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.pushNotification.type),
      withLatestFrom(
        this._store$.select(CoreReducer.getNotifications),
      ),
      switchMap(([action, notifications]) => {
        const list = Object.assign([], notifications);
        list.push(...this._notificationService.update([action['notification']]));
        return of(NotificationActions.pushNotificationSuccess({notifications: this._notificationService.sortNotifications(list)}));
        }
      )
    )
  );

  /**
   * Toasts only what happens while the user is logged in. The first list after logging in is what
   * was already there, so it sets the baseline and is never announced; after that, a notification
   * above the highest id seen is one that arrived since. Ids come from the server, so no browser
   * clock is involved, and scrolling to older pages or filtering never brings back a higher id.
   */
  announceNewNotifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        NotificationActions.getNotificationsSuccess,
        NotificationActions.getNotificationByIdsSuccess,
        NotificationActions.pushNotificationSuccess
      ),
      tap((action: any) => this._announceNewNotifications(action.notifications ?? []))
    ),
    { dispatch: false }
  );

  /** Logging out starts over: the next user's first list is their baseline, not news. */
  resetAnnouncementsOnLogout$ = createEffect(() =>
    this._loggedOut$.pipe(
      tap(() => this._newestNotificationId = null)
    ),
    { dispatch: false }
  );

  private _loader: LoadingBarState;
  /** The highest notification id seen this session; null until the first list, which is never announced. */
  private _newestNotificationId: number | null = null;

  /** Loads still in flight when the user logs out would otherwise land in the next session. */
  private get _loggedOut$() {
    return this._actions$.pipe(ofType(AuthApiActions.logoutSuccess, AuthApiActions.logoutFailure, SigninPageActions.autoLogout));
  }

  constructor(
    private _actions$: Actions,
    private _store$: Store<any>,
    private _notificationService: NotificationService,
    private _loadingBar: LoadingBarService,
    private _snackBarService: SnackBarService,
    private _storageService: StorageService,
    private _drawerService: NotificationsDrawerService
  ) {
    this._loader = this._loadingBar.useRef('main');
  }

  private _announceNewNotifications(notifications: Notification[]): void {
    const newestId = Math.max(this._newestNotificationId ?? 0, ...notifications.map(n => n.notificationId ?? 0));

    if (this._newestNotificationId === null) {
      this._newestNotificationId = newestId;
      return;
    }

    const fresh = notifications.filter(n =>
      n.notificationId > this._newestNotificationId && n.notificationStatus !== NotificationStatus.READ);
    this._newestNotificationId = newestId;

    // Clicking a toast opens the drawer, where the notification is waiting.
    const onClick = () => this._drawerService.open();

    if (fresh.length === 1) {
      this._announce(fresh[0], onClick);
    } else if (fresh.length > 1) {
      // A burst would replace itself toast by toast, so it is announced once, with the count.
      this._snackBarService.open('notifications.toast.newNotifications', {
        noTranslateText: `${fresh.length}`,
        tone: fresh.some(n => this._isAlarming(n)) ? 'warn' : 'success',
        onClick,
      });
    }
  }

  /**
   * One notification, titled as the list titles it. A report's run is told apart from the user's
   * other runs of the same report by how many objects it covers and when it was requested; a shared
   * link by its name. These toasts are the only ones such events raise: the modules no longer
   * announce them on their own.
   */
  private _announce(notification: Notification, onClick: () => void): void {
    const titleKey = notificationTitleKey(notification);
    const tone = this._isAlarming(notification) ? 'warn' : 'success';

    if (!titleKey) {
      this._snackBarService.open('notifications.toast.newNotification', { noTranslateText: notification.title, tone, onClick });
      return;
    }

    const data = notification.data;
    const details: SnackBarText[] = [];
    const unitCount = data?.parameters?.unitsNames?.length ?? 0;
    if (unitCount) {
      details.push({ key: 'notifications.toast.reportObjects', params: { count: unitCount } });
    }
    if (data?.requestedAt) {
      details.push({ key: 'notifications.toast.reportRequestedAt', params: { date: this._formatDate(data.requestedAt) } });
    }
    const shareLink = shareLinkAccess(notification);
    if (shareLink?.name) {
      details.push({ key: 'notifications.toast.shareLinkName', params: { name: shareLink.name } });
    }

    this._snackBarService.open(titleKey, { params: { name: notification.title }, details, tone, onClick });
  }

  /** In the user's chosen time zone, as the customDate pipe shows dates everywhere else. */
  private _formatDate(value: string): string {
    const timeZone = this._storageService.get('timeZone') || moment.tz.guess();
    return moment(value).tz(timeZone).format('DD.MM.YYYY HH:mm');
  }

  private _isAlarming(notification: Notification): boolean {
    return notification.notificationSeverity === NotificationSeverity.ERROR
      || notification.notificationSeverity === NotificationSeverity.WARNING;
  }
}
